// Header.jsx
import React, { useEffect, useState } from "react";
import { Link, animateScroll as scroll } from "react-scroll";
import { FiMenu, FiX } from "react-icons/fi";

const DARK_SECTIONS = ["hero", "motion", "philosophy"];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renderTheme, setRenderTheme] = useState("light");

  const navItems = [
    { name: "Home", to: "hero" },
    { name: "About", to: "about" },
    { name: "Journey", to: "timeline" },
    { name: "Life in Motion", to: "motion" },
    { name: "Speaking", to: "speaking" },
    { name: "Philosophy", to: "philosophy" },
    { name: "Connect", to: "connect" },
  ];

  useEffect(() => {
    let timeout;

    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      let current = "hero";

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 120 && rect.bottom >= 120) {
          current = section.id;
        }
      });

      const isDarkSection = DARK_SECTIONS.includes(current);
      const newTheme = isDarkSection ? "light" : "dark";

      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setRenderTheme(newTheme);
      }, 120);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMenuOpen(false);
      }
    };

    const handleBodyScroll = () => {
      document.body.style.overflow = menuOpen ? "hidden" : "";
    };

    handleBodyScroll();
    window.addEventListener("resize", handleResize);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("resize", handleResize);
    };
  }, [menuOpen]);

  const scrollToTop = () => {
    setMenuOpen(false);
    scroll.scrollToTop({ duration: 500 });
  };

  const isDark = renderTheme === "dark";

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-all duration-700 ease-in-out ${
        isDark
          ? "bg-black/90 text-white"
          : "bg-white/90 text-black shadow-md"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <button
          onClick={scrollToTop}
          className={`cursor-pointer text-2xl font-bold transition ${
            isDark ? "text-white" : "text-black"
          }`}
          aria-label="Go to top"
        >
          Kenneth Milanzi
        </button>

        <nav className="hidden space-x-8 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              smooth={true}
              duration={500}
              offset={-80}
              spy={true}
              className={`cursor-pointer pb-1 transition ${
                isDark
                  ? "text-white/80 hover:text-white"
                  : "text-black/70 hover:text-black"
              }`}
              activeClass="font-semibold border-b-2 border-current"
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <button
          className={`text-2xl focus:outline-none md:hidden ${
            isDark ? "text-white" : "text-black"
          }`}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          type="button"
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      <div
        id="mobile-menu"
        className={`overflow-hidden transition-all duration-300 md:hidden ${
          menuOpen ? "max-h-[calc(100vh-72px)]" : "max-h-0"
        }`}
      >
        <div
          className={`border-t ${
            isDark
              ? "border-white/10 bg-black/95 text-white"
              : "border-black/10 bg-white/95 text-black"
          } overflow-y-auto`}
          style={{ maxHeight: "calc(100vh - 72px)" }}
        >
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.to}
              smooth={true}
              duration={500}
              offset={-80}
              spy={true}
              className={`block cursor-pointer border-b px-6 py-4 transition ${
                isDark
                  ? "border-white/10 hover:bg-white/10"
                  : "border-black/10 hover:bg-black/5"
              }`}
              activeClass="font-semibold"
              onClick={() => setMenuOpen(false)}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;